/**
 * @velyn-custom — vely-setup-details.js
 * Aligned with vely-customer-reviews / vely-new-products-slider:
 * - Manual VelySwiperPagination.refresh (no Swiper pagination:getConfig)
 * - Config read from .vely-sd__swiper data-* (same as reviews)
 * - No static imports — dynamic import only when opening modal so carousel always inits
 */
const getSwiper = () => window.Swiper;
const HOVER_MQ = "(hover: hover) and (pointer: fine)";
const MOBILE_MQ = "(max-width: 767.98px)";

class VelySetupDetails extends HTMLElement {
  connectedCallback() {
    if (this._velySdInitStarted) return;
    this._velySdInitStarted = true;

    this.bindModal();
    this.bindHoverVideos();

    if (this.querySelector(".vely-sd__swiper")) {
      this.waitForSwiper(() => this.initCarousel());
    } else {
      this.classList.add("vely-sd--ready");
    }
  }

  disconnectedCallback() {
    this.pauseAllCardVideos();
    this.unbindHoverVideos();
    this._sdRetryTimers?.forEach((id) => clearTimeout(id));
    this._sdRetryTimers = null;
    if (this._sdResizeObserver) {
      this._sdResizeObserver.disconnect();
      this._sdResizeObserver = null;
    }
    if (this._swiper) {
      try {
        this._swiper.destroy(true, true);
      } catch (_e) {
        /* ignore */
      }
      this._swiper = null;
    }
    this._velySdInitStarted = false;
    this._velySdModalBound = false;
    this._hoverBound = false;
    this.classList.remove("vely-sd--ready");
  }

  waitForSwiper(callback, attempt = 0) {
    const hasSwiper = typeof getSwiper() === "function";
    const hasPagination = Boolean(window.VelySwiperPagination?.refresh);
    if (hasSwiper && hasPagination) {
      callback();
      return;
    }
    if (attempt > 160) {
      console.warn(
        "[vely-setup-details] Swiper or VelySwiperPagination not available"
      );
      return;
    }
    setTimeout(() => this.waitForSwiper(callback, attempt + 1), 50);
  }

  isCarouselActiveForViewport() {
    const isMobile = window.matchMedia(MOBILE_MQ).matches;
    if (isMobile) {
      return this.dataset.carouselMobile === "true";
    }
    return this.dataset.carouselDesktop === "true";
  }

  paginationEnabled() {
    return this.dataset.showPagination === "true";
  }

  countDomSlides() {
    return this.querySelectorAll(
      ".vely-sd__swiper .swiper-wrapper > .swiper-slide:not(.swiper-slide-duplicate)"
    ).length;
  }

  /**
   * Peek carousels (items_per_row_mobile ≈ 1.2) need one bullet per card —
   * same UX as other Velyn side-sliders. Do NOT trust snapGrid here: on this
   * section (mobile-only carousel + desktop grid) snapGrid often stays at 1
   * and leaves a single empty/gray oval track under a multi-slide swiper.
   */
  expectedBulletCount() {
    const slides = this.countDomSlides();
    if (slides <= 1) return slides;

    const spvRaw = this._swiper?.params?.slidesPerView;
    const spv =
      typeof spvRaw === "number"
        ? spvRaw
        : typeof spvRaw === "string" && spvRaw !== "auto"
          ? parseFloat(spvRaw) || 1
          : 1;

    if (spv < 2) return slides;
    if (spv >= slides) return 1;
    return Math.max(1, slides - Math.floor(spv) + 1);
  }

  needsPagination() {
    return this.paginationEnabled() && this.countDomSlides() > 1;
  }

  refreshPagination() {
    // Setting off → no pagination HTML; carousel still works independently
    if (!this.paginationEnabled() || !this._swiper || !this._paginationEl) {
      return;
    }

    this._swiper.updateSize?.();
    this._swiper.updateSlides?.();
    this._swiper.updateProgress?.();
    this._swiper.update?.();

    const carouselActive = this.isCarouselActiveForViewport();
    const enabled = this._swiper.enabled !== false;

    if (!carouselActive || !enabled) {
      this._paginationEl.innerHTML = "";
      this.syncControls(this._swiper);
      return;
    }

    const expected = this.expectedBulletCount();
    // Always force rebuild from DOM slide count — never leave a stale 1-bullet oval
    if (window.VelySwiperPagination?.forceRebuild) {
      window.VelySwiperPagination.forceRebuild(
        this._swiper,
        this._paginationEl,
        expected
      );
    } else {
      this._paginationEl.innerHTML = "";
      window.VelySwiperPagination?.refresh?.(
        this._swiper,
        this._paginationEl,
        expected
      );
    }

    this.syncControls(this._swiper);
  }

  syncControls(swiper) {
    const controls = this.querySelector("[data-vely-pagination-anchor]");
    if (!controls || !swiper) return;

    const carouselActive = this.isCarouselActiveForViewport();
    const shouldShow =
      this.paginationEnabled() &&
      carouselActive &&
      swiper.enabled !== false &&
      this.needsPagination();

    controls.hidden = !shouldShow;
    controls.setAttribute("aria-hidden", shouldShow ? "false" : "true");
  }

  initCarousel() {
    const SwiperCtor = getSwiper();
    if (typeof SwiperCtor !== "function") return;

    const swiperEl = this.querySelector(".vely-sd__swiper");
    if (!swiperEl) return;

    if (this._swiper) {
      this._swiper.destroy(true, true);
      this._swiper = null;
    }

    const num = (val, fallback) => {
      const n = parseFloat(val);
      return Number.isFinite(n) ? n : fallback;
    };

    // Same as customer-reviews: read config from swiper element data-*
    const gapDesktop = num(swiperEl.dataset.spacing, 24);
    const gapMobile = num(swiperEl.dataset.spacingMobile, 16);
    const speed = num(swiperEl.dataset.speed, 500);
    const perDesktop = num(swiperEl.dataset.desktop, 3);
    const perTablet = num(swiperEl.dataset.tablet, 2);
    const perMobile = num(swiperEl.dataset.mobile, 1.2);
    const useLoop = this.dataset.loop === "true";
    const autoplayOn = swiperEl.dataset.autoplay === "true";
    const carouselDesktop = this.dataset.carouselDesktop === "true";
    const carouselMobile = this.dataset.carouselMobile === "true";

    let autoplay = false;
    if (autoplayOn) {
      autoplay = {
        delay: num(swiperEl.dataset.autoPlaySpeed, 5000),
        pauseOnMouseEnter: true,
      };
    }

    const nextEl = this.querySelector(".swiper-button-next");
    const prevEl = this.querySelector(".swiper-button-prev");
    this._paginationEl = this.querySelector(".vely-swiper-pagination-pill");

    // Omit pagination — Swiper 11 crashes on pagination:false; bullets via VelySwiperPagination
    try {
      this._swiper = new SwiperCtor(swiperEl, {
        speed,
        loop: useLoop,
        autoplay,
        grabCursor: true,
        watchSlidesProgress: true,
        observer: true,
        observeParents: true,
        spaceBetween: gapMobile,
        slidesPerView: perMobile,
        enabled: carouselMobile,
        navigation: nextEl && prevEl ? { nextEl, prevEl } : undefined,
        breakpoints: {
          768: {
            spaceBetween: gapDesktop,
            slidesPerView: perTablet,
            enabled: carouselDesktop,
          },
          1025: {
            spaceBetween: gapDesktop,
            slidesPerView: perDesktop,
            enabled: carouselDesktop,
          },
        },
        on: {
          init: (swiper) => {
            swiper.update();
            this.refreshPagination();
          },
          slideChange: (swiper) => {
            this.pauseAllCardVideos();
            if (!this._paginationEl) return;
            window.VelySwiperPagination?.setActiveBullet?.(
              swiper,
              this._paginationEl
            );
          },
          resize: () => {
            this._swiper?.updateSize?.();
            this._swiper?.updateSlides?.();
            this._swiper?.updateProgress?.();
            this.refreshPagination();
          },
          breakpoint: (swiper) => {
            this.pauseAllCardVideos();
            swiper.updateSize?.();
            swiper.updateSlides?.();
            swiper.updateProgress?.();
            swiper.update();
            this.refreshPagination();
          },
          slidesLengthChange: () => this.refreshPagination(),
        },
      });
    } catch (error) {
      this._swiper = null;
      console.warn("[vely-setup-details] Swiper init failed", error);
      return;
    }

    this.refreshPagination();
    requestAnimationFrame(() => {
      this._swiper?.updateSize?.();
      this._swiper?.updateSlides?.();
      this._swiper?.update?.();
      this.refreshPagination();
    });

    // Mobile-first: slide widths can still settle after fonts/images load.
    // Re-run refresh after short delays so a stale 1-bullet track can rebuild
    // once getPageCount sees the full slide list.
    this._sdRetryTimers?.forEach((id) => clearTimeout(id));
    this._sdRetryTimers = [150, 400, 900].map((delay) =>
      setTimeout(() => {
        this._swiper?.updateSize?.();
        this._swiper?.updateSlides?.();
        this._swiper?.update?.();
        this.refreshPagination();
      }, delay)
    );

    if (window.ResizeObserver) {
      this._sdResizeObserver?.disconnect();
      this._sdResizeObserver = new ResizeObserver(() => {
        this._swiper?.update?.();
        this.refreshPagination();
      });
      this._sdResizeObserver.observe(swiperEl);
    }

    this.classList.add("vely-sd--ready");
  }

  /* ---------- Card video hover ---------- */
  canHoverPlay() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return false;
    }
    return window.matchMedia(HOVER_MQ).matches;
  }

  bindHoverVideos() {
    if (this._hoverBound) return;
    this._hoverBound = true;

    this._onVideoEnter = (event) => {
      if (!this.canHoverPlay()) return;
      this.playCardVideo(event.currentTarget);
    };
    this._onVideoLeave = (event) => {
      this.pauseCardVideo(event.currentTarget);
    };

    this.querySelectorAll("[data-vely-sd-hover-video]").forEach((media) => {
      media.addEventListener("mouseenter", this._onVideoEnter);
      media.addEventListener("mouseleave", this._onVideoLeave);
      media.addEventListener("focusin", this._onVideoEnter);
      media.addEventListener("focusout", this._onVideoLeave);
    });
  }

  unbindHoverVideos() {
    if (!this._hoverBound) return;
    this.querySelectorAll("[data-vely-sd-hover-video]").forEach((media) => {
      media.removeEventListener("mouseenter", this._onVideoEnter);
      media.removeEventListener("mouseleave", this._onVideoLeave);
      media.removeEventListener("focusin", this._onVideoEnter);
      media.removeEventListener("focusout", this._onVideoLeave);
    });
    this._hoverBound = false;
  }

  playCardVideo(media) {
    const video = media?.querySelector?.("video.vely-sd__tile-video");
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    video.loop = true;
    const playPromise = video.play();
    if (playPromise?.then) {
      playPromise
        .then(() => media.classList.add("is-playing"))
        .catch(() => {});
    } else {
      media.classList.add("is-playing");
    }
  }

  pauseCardVideo(media) {
    const video = media?.querySelector?.("video.vely-sd__tile-video");
    if (!video) return;
    video.pause();
    try {
      video.currentTime = 0;
    } catch (_) {}
    media.classList.remove("is-playing");
  }

  pauseAllCardVideos() {
    this.querySelectorAll("[data-vely-sd-hover-video]").forEach((media) => {
      this.pauseCardVideo(media);
    });
  }

  /* ---------- Modal (dynamic import — does not block carousel init) ---------- */
  bindModal() {
    if (this._velySdModalBound) return;
    this._velySdModalBound = true;

    this.querySelectorAll("[data-vely-sd-open]").forEach((btn) => {
      btn.dataset.velySdBound = "1";
      btn.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openModal(btn);
      });
    });

    this.addEventListener("click", (event) => {
      const trigger = event.target.closest("[data-vely-sd-open]");
      if (trigger && this.contains(trigger) && !trigger.dataset.velySdBound) {
        event.preventDefault();
        this.openModal(trigger);
      }
    });
  }

  async openModal(trigger) {
    this.pauseAllCardVideos();

    const slide = trigger.closest(".vely-sd__slide");
    const tpl = slide?.querySelector("template.vely-sd__tpl");
    if (!tpl) return;

    let eventModal;
    let NextSkyTheme;
    try {
      const modalMod = await import("@NextSkyTheme/modal");
      NextSkyTheme = await import("@NextSkyTheme/global");
      eventModal = modalMod.eventModal;
    } catch (error) {
      console.warn("[vely-setup-details] modal import failed", error);
      return;
    }

    const clone = tpl.content.cloneNode(true);
    const popup = clone.querySelector("vely-sd-popup");
    if (!popup) return;

    NextSkyTheme.getBody().appendChild(popup);

    const modalVideo = popup.querySelector("video.vely-sd-modal__video");
    if (modalVideo) {
      modalVideo.muted = true;
      modalVideo.playsInline = true;
      modalVideo.setAttribute("playsinline", "");
    }

    setTimeout(() => {
      eventModal(popup, "open", true, null, false);
      if (modalVideo) {
        modalVideo.play()?.catch?.(() => {});
      }
    }, 50);

    NextSkyTheme.global.rootToFocus = trigger;
  }
}

if (!customElements.get("vely-setup-details")) {
  customElements.define("vely-setup-details", VelySetupDetails);
}

document.addEventListener("shopify:section:load", (event) => {
  const host = event.target?.querySelector?.("vely-setup-details");
  if (host) {
    host._velySdInitStarted = false;
    host._velySdModalBound = false;
    host._hoverBound = false;
    host.connectedCallback();
  }
});
/** @velyn-custom END — vely-setup-details.js */
